import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, LogOut } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import AdminMessages from "@/pages/AdminMessages";
import AdminProducts from "@/pages/AdminProducts";
import AdminReports from "@/pages/AdminReports";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("mensagens");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setAuth(true);
      } catch {
        setAuth(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      setAuth(true);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* sessão já inválida */
    }
    setUser(null);
    setAuth(false);
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-parchment">
        <Loader2 className="h-8 w-8 animate-spin text-olive" />
      </div>
    );
  }

  if (auth === false) {
    return (
      <div
        data-testid="admin-login-screen"
        className="flex min-h-screen items-center justify-center bg-forest px-6 py-12"
      >
        <div className="w-full max-w-md rounded-[2rem] border border-parchment/10 bg-nightcard p-8 text-center shadow-2xl sm:p-10">
          <div className="mb-6 flex justify-center">
            <LogoMark dark size={52} />
          </div>
          <h1 className="font-display text-3xl font-medium text-parchment">Área do Lojista</h1>
          <p className="mt-3 text-sm leading-relaxed text-parchment/60">
            Acesso exclusivo da equipe Agro Carmo para gerenciar produtos e mensagens do site.
          </p>

          <button
            type="button"
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-parchment px-6 py-4 text-sm font-semibold text-ink transition-transform duration-300 hover:scale-[1.02]"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            Entrar com Google
          </button>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-parchment/15" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-parchment/40">
              ou com e-mail
            </span>
            <span className="h-px flex-1 bg-parchment/15" />
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4 text-left">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-mail"
              data-testid="admin-email-input"
              className="w-full rounded-xl border border-parchment/15 bg-forest px-4 py-3 text-sm text-parchment outline-none transition-colors placeholder:text-parchment/40 focus:border-ochre"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha"
              data-testid="admin-password-input"
              className="w-full rounded-xl border border-parchment/15 bg-forest px-4 py-3 text-sm text-parchment outline-none transition-colors placeholder:text-parchment/40 focus:border-ochre"
            />
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm text-clay">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-email-login-button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-clay px-6 py-4 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-ochre hover:text-forest disabled:opacity-60"
            >
              {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
              Entrar
            </button>
          </form>

          <Link
            to="/"
            data-testid="admin-back-to-site"
            className="mt-6 inline-flex items-center gap-2 text-sm text-parchment/50 transition-colors hover:text-ochre"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-parchment">
      <header className="sticky top-0 z-40 border-b border-linen bg-parchment/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="admin-logo-link" aria-label="Voltar ao site">
            <LogoMark />
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden items-center gap-3 sm:flex">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="h-9 w-9 rounded-full border border-linen object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-olive font-display text-sm font-semibold text-parchment">
                  {user?.name?.charAt(0) || "A"}
                </span>
              )}
              <div className="leading-tight">
                <p className="text-sm font-semibold text-ink">{user?.name}</p>
                <p className="text-xs text-moss">{user?.email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-sm font-medium text-olive transition-colors hover:bg-linen/50"
            >
              <LogOut className="h-4 w-4" />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8">
          <p className="mb-2 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-clay">
            Área do Lojista
          </p>
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">
            Painel da loja
          </h1>
        </div>

        <div className="mb-8 flex gap-2" role="tablist" aria-label="Seções do painel">
          {[
            { slug: "mensagens", label: "Mensagens" },
            { slug: "produtos", label: "Produtos" },
            { slug: "relatorios", label: "Relatórios" },
          ].map((t) => (
            <button
              key={t.slug}
              type="button"
              role="tab"
              aria-selected={tab === t.slug}
              data-testid={`admin-tab-${t.slug}`}
              onClick={() => setTab(t.slug)}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                tab === t.slug ? "bg-olive text-parchment" : "text-olive hover:bg-linen/60"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "mensagens" && <AdminMessages />}
        {tab === "produtos" && <AdminProducts />}
        {tab === "relatorios" && <AdminReports />}
      </main>
    </div>
  );
}
